import Header from '@/components/Header'
import useDocumentTitle from '@/components/Helmet'
import { useCustomLoader } from '@/components/Loader'
import GlobalDialog from '@/components/material/Dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import axiosInstance from '@/lib/axiosInstance'
import { gradientColors, userTypeColors } from '@/lib/utils'
import { ExpandMore } from '@mui/icons-material'
import { Accordion, AccordionDetails, AccordionSummary, Avatar, Box, Chip, Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Tooltip, Typography, useMediaQuery } from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'

const AdminUsers = () => {
    useDocumentTitle("Admin Users")
    const { toast } = useToast()
    const loader = useCustomLoader();
    const isMobile = useMediaQuery("(max-width: 600px)");

    const [users, setUsers] = useState([]);
    const [page, setPage] = useState(0);
    const [limit, setLimit] = useState(10);
    const [counts, setCounts] = useState(0);
    const [search, setSearch] = useState("");
    const [userType, setUserType] = useState("");
    const [expanded, setExpanded] = React.useState<string | false>(false);

    const [open, setOpen] = useState<any>(null);

    const onClose = () => {
        setOpen(null);
    };


    const fetchUsers = useCallback(async (search: any) => {
        loader(true);
        try {
            await axiosInstance.get('/api/admin/users', {
                params: {
                    page: page + 1,
                    limit,
                    search,
                    userType: (userType && userType !== "all") ? JSON.stringify([userType]) : ""
                }
            })
                .then((res) => {
                    setUsers(res.data.users);
                    setCounts(res.data.count);
                })

        } catch (error) {
            toast({
                variant: "destructive",
                title: "Error!",
                description: "Something went wrong",
            })
        }
        finally {
            loader(false);
        }
    }, [page, limit, userType])

    useEffect(() => {
        const timeoute = setTimeout(() => {
            fetchUsers(search)
        }, 600)
        return () => clearTimeout(timeoute);
    }, [fetchUsers, search])


    const handleChangePage = (
        event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number,
    ) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {

        console.log(event.target.value)
        setLimit(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleStatusChange = async () => {
        try {
            await axiosInstance.put(`/api/admin/users/${open?._id}`, {
                isActive: !open?.isActive
            })
                .then((res) => {
                    if (res?.data?.success) {
                        toast({
                            variant: "success",
                            title: "Success!",
                            description: res.data.message,
                        })
                        fetchUsers(search);
                        onClose();
                    }
                    else {
                        toast({
                            variant: "destructive",
                            title: "Error!",
                            description: res.data.message,
                        })
                    }
                })
        } catch (error) {
            toast({
                variant: "destructive",
                title: "Error!",
                description: "Something went wrong",
            })
        }
    }
    return (
        <div className="min-h-screen bg-gray-50">
            <Header />
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <Paper elevation={0} className="p-2">
                    <Box className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-2">
                        <h1 className="text-3xl font-bold text-gray-900 mt-3 ml-3">Users</h1>
                        <Box className="flex flex-col sm:flex-row gap-3">
                            <Box>
                                <Label htmlFor="search">Search</Label>
                                <Input
                                    type="search"
                                    name="search"
                                    id="search"
                                    placeholder="Search by name, email, phone"
                                    onChange={(e) => setSearch(e.target.value)}
                                    value={search}
                                    className='w-full md:w-64'
                                />
                            </Box>
                            <Box>
                                <Label htmlFor="userType">Select User Type</Label>
                                <Select onValueChange={(e) => setUserType(e)} value={userType}>
                                    <SelectTrigger className="text-gray-900 text-sm w-full md:w-48" id='userType' data-testid="select-user-type" >
                                        <SelectValue placeholder="Select User Type" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">
                                            All
                                        </SelectItem>
                                        <SelectItem value="buyer">
                                            Buyer
                                        </SelectItem>
                                        <SelectItem value="seller">
                                            Seller
                                        </SelectItem>
                                        <SelectItem value="agent">
                                            Agent
                                        </SelectItem>
                                    </SelectContent>
                                </Select>
                            </Box>
                        </Box>
                    </Box>
                    <TableContainer component={Paper}>
                        {isMobile ? <Box>
                            {
                                users?.map((user: any, index) => (
                                    <Accordion 
                                        expanded={expanded === user?._id} 
                                        key={user._id} 
                                        onChange={() => setExpanded(expanded === user?._id ? null : user?._id)} 
                                        className='mb-2'
                                    >
                                        <AccordionSummary
                                            expandIcon={<ExpandMore />}
                                            aria-controls="panel1-content"
                                            id="panel1-header"
                                            sx={{
                                                "& .MuiAccordionSummary-content": {
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 1,               // optional spacing
                                                },
                                                backgroundColor : "#f9fafb",
                                                // background: gradientColors[index % gradientColors.length],
                                                // color: "#fff", // optional: text contrast
                                                borderRadius: 2, // optional: modern look
                                            }}
                                        >
                                            {/* Left: avatar + name */}
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                <Avatar src={user?.avatar} alt="" />
                                                <Typography sx={{ fontWeight: 600, fontSize: 18 }}>
                                                    {user.firstName} {user?.lastName}
                                                </Typography>
                                            </Box>

                                            {/* Right: chip (pushed by ml:auto) */}
                                            <Chip
                                                label={user.userType}
                                                size="small"
                                                sx={{ ml: "auto", width: 100, height: 30 }}
                                                color={userTypeColors[user.userType]}
                                            />
                                        </AccordionSummary>

                                        <AccordionDetails>
                                            <Grid container spacing={2}>
                                                <Grid size={{ xs: 6 }}>
                                                    <span >Email:</span><br />
                                                    <span className="font-semibold break-words overflow-hidden text-ellipsis block">{user.email}</span>
                                                </Grid>
                                                <Grid size={{ xs: 6 }}>
                                                    <span >Phone:</span><br />
                                                    <span className="font-semibold">{user.phone || "N/A"}</span>
                                                </Grid>
                                                <Grid size={{ xs: 6 }}>
                                                    <span >User Type:</span><br />
                                                    <span className="font-semibold">
                                                        <Chip
                                                            label={user.userType}
                                                            size='small'
                                                            sx={{ width: 100, height: 30 }}
                                                            color={userTypeColors[user.userType]}
                                                        />
                                                    </span>
                                                </Grid>
                                                <Grid size={{ xs: 6 }}>
                                                    <span >Status:</span><br />
                                                    <span className="font-semibold">
                                                        <Chip
                                                            label={user.isActive ? "Active" : "Inactive"}
                                                            size='small'
                                                            sx={{ width: 100, cursor: "pointer", height: 30 }}
                                                            color={user.isActive ? "success" : "error"}
                                                            onClick={() => setOpen(user)}
                                                        />
                                                    </span>
                                                </Grid>
                                            </Grid>
                                        </AccordionDetails>
                                    </Accordion>
                                ))
                            }
                        </Box> :

                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Image</TableCell>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Email</TableCell>
                                        <TableCell>Phone</TableCell>
                                        <TableCell>User Type</TableCell>
                                        <TableCell>Status</TableCell>
                                        {/* <TableCell>Action</TableCell> */}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {users?.map((user: any) => (
                                        <TableRow key={user._id}>
                                            <TableCell>
                                                {
                                                    user.avatar ?
                                                        <img src={user.avatar} alt={user.firstName} className='w-12 h-10 object-cover rounded-lg' />
                                                        : " No Image"
                                                }
                                            </TableCell>
                                            <TableCell>{user.firstName} {user?.lastName}</TableCell>
                                            <TableCell>{user.email}</TableCell>
                                            <TableCell>{user.phone || "N/A"}</TableCell>
                                            <TableCell sx={{ textTransform: "capitalize" }}>

                                                <Chip
                                                    label={user.userType}
                                                    size='small'
                                                    sx={{ width: 100 }}
                                                    color={userTypeColors[user.userType]}
                                                />

                                            </TableCell>
                                            <TableCell sx={{ textTransform: "capitalize" }}>
                                                <Tooltip title="Update Status" placement='top'>
                                                    <Chip
                                                        label={user.isActive ? "Active" : "Inactive"}
                                                        size='small'
                                                        sx={{ width: 100, cursor: "pointer" }}
                                                        color={user.isActive ? "success" : "error"}
                                                        onClick={() => setOpen(user)}
                                                    />
                                                </Tooltip>

                                            </TableCell>
                                            {/* <TableCell >
                                            <Box sx={{ display: "flex", gap: 2 }}>
                                                <Tooltip title="View Property">
                                                    <Eye className='text-green-600 cursor-pointer' onClick={() => navigate(`/admin/property/${user._id}`)} />
                                                </Tooltip>
                                            </Box>
                                        </TableCell> */}
                                        </TableRow>
                                    ))}
                                    {
                                        users?.length === 0 &&
                                        <TableRow>
                                            <TableCell colSpan={7} sx={{ textAlign: "center" }}>No users found</TableCell>
                                        </TableRow>
                                    }
                                </TableBody>
                            </Table>
                        }
                        <TablePagination
                            component="div"
                            count={counts}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={limit}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </TableContainer>
                </Paper>
                <GlobalDialog
                    open={open}
                    title="Update Status"
                    content={`Are you sure you want to update status to ${open?.isActive ? "In Active" : "Active"}?`}
                    onClose={onClose}
                    onConfirm={handleStatusChange}
                    confirmText="Update"
                />
            </section>
        </div>
    )
}

export default AdminUsers